/**
 * Shared UI chrome: common strings, navigation, friends, notifications, etc.
 */
import { spaceManageMembers } from './spaceManageMembers';

export const core = {
  common: {
    loading: 'Loading...',
    error: 'Error',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    remove: 'Remove',
    notSet: 'Not set',
    retry: 'Retry',
    duplicate: 'Duplicate',
    share: 'Share',
    copy: 'Copy',
    comingSoon: 'Coming Soon',
    previous: 'Previous',
  },

  sessionLifetime: {
    title: 'Session maximum',
    description:
      'For security, you are signed out after this many days even if you keep using Adieuu. This stops a stolen session from staying signed in forever. Activity only extends the session up to this limit. Other devices use the same maximum but their own sign-in date.',
    label: 'Maximum session age',
    dayOne: '1 day',
    days: '{{count}} days',
    forcedLogout: 'This device will be signed out on {{date}}.',
    logoutNow: 'Log out now',
    confirmTitle: 'Sign out now?',
    confirmBody:
      'This session is already older than {{count}} days. Changing to this maximum will sign you out immediately.',
    confirmAction: 'Sign out',
    updateError: 'Could not update the session maximum. Please try again.',
    loadError: 'Could not load the session maximum.',
  },

  // Navigation
  nav: {
    home: 'Home',
    about: 'About',
    more: 'More',
    legalPolicies: 'Legal Policies',
    getDesktopApp: 'Get Desktop App',
    loginPrompt: 'Login or Create Account',
    backToHome: 'Back to Home',
    account: 'Account',
    logout: 'Logout',
    expandSidebar: 'Expand Sidebar',
    collapseSidebar: 'Collapse Sidebar',
    resizeSidebar: 'Drag to resize navigation',
    goBack: 'Go back',
    goForward: 'Go forward',
    pageFallback: 'Page',
    friends: 'Friends',
    notifications: 'Notifications',
    notificationsUnseen: '{{count}} Unseen Notification(s)',
    notificationsUnseen_one: 'Notifications, 1 unseen',
    notificationsUnseen_other: 'Notifications, {{count}} unseen',
    friendRequests: '{{count}} Friend Request(s)',
    friendRequests_one: '1 Friend Request',
    friendRequests_other: '{{count}} Friend Requests',
    chatInvitations: '{{count}} Chat Invitation(s)',
    chatInvitations_one: '1 Chat Invitation',
    chatInvitations_other: '{{count}} Chat Invitations',
    spaceInvitations: '{{count}} Space Invitation(s)',
    spaceInvitations_one: '1 Space Invitation',
    spaceInvitations_other: '{{count}} Space Invitations',
    invitations: '{{count}} Invitation(s)',
    invitations_one: '1 Invitation',
    invitations_other: '{{count}} Invitations',
    inviteHistory: 'Invite History',
  },

  invitationInbox: {
    bucketPending: 'Pending ({{count}})',
    bucketDeclined: 'Declined ({{count}})',
    bucketExpired: 'Expired ({{count}})',
    bucketAccepted: 'Accepted ({{count}})',
    statusAria: 'Invitation status',
    filterAria: 'Sort invitations',
    sortName: 'Name',
    sortRecency: 'Recency',
    sortExpiration: 'Expiration',
    sortAsc: 'Ascending',
    sortDesc: 'Descending',
    emptyPending: 'No pending invitations',
    emptyDeclined: 'No declined invitations',
    emptyExpired: 'No expired invitations',
    emptyAccepted: 'No accepted invitations',
    page: '{{page}} / {{totalPages}}',
    declinedAt: 'Declined: {{when}}',
    expiredAt: 'Expired: {{when}}',
    revokedAt: 'Revoked: {{when}}',
  },

  // Sidebar sections
  sidebar: {
    main: 'Main',
    account: 'Account',
    identity: 'Alias',
    comingSoon: '{{feature}} coming soon.',
    tabs: {
      spaces: 'Spaces',
    },
    conversationsTab: 'Conversations',
    spacesTab: 'Spaces',
    allTab: 'Show All',
    newConversation: 'Start New',
    signInForConversations: 'Sign into an Alias to see Conversations',
    discoverSpaces: 'Discover',
    noSpaces: "You haven't joined any Spaces yet",
    signInForSpaces: 'Sign into an Alias to see Spaces',
    update: {
      available: 'Update Available',
      downloading: 'Downloading Update',
      install: 'Install Update',
      refreshWeb: 'Refresh to Update',
      error: 'Update issue',
    },
  },

  // Search
  search: {
    title: 'Search',
    subtitle: 'Find people and Spaces.',
    discoverTitle: 'Discover',
    discoverSubtitle: 'Browse Spaces and people.',
    placeholder: 'Search people or Spaces...',
    clear: 'Clear search',
    publicPlaceholder: 'Search public content...',
    noResults: 'No results found.',
    noResultsHint: 'Try a different search term or check the spelling.',
    hint: 'Enter a name, username, or Space to search.',
    discoverHint: 'Random people to browse — try a search to find someone specific.',
    spacesDirectoryHint: 'Browse public Spaces, or search by name.',
    discoverPeople: 'Random people',
    discoverSpaces: 'Spaces',
    recentSearches: 'Recent searches',
    yourRecentSearches: 'Your recent searches',
    platformRecent: 'Recent on Adieuu',
    platformTrending: 'Trending on Adieuu',
    clearRecentSearches: 'Clear',
    removeRecentSearch: 'Remove {{query}} from recent searches',
    viewAll: 'View all results',
    resultsCount: '{{count}} result(s) found',
    resultsCount_one: '1 result found',
    resultsCount_other: '{{count}} results found',
    sections: {
      people: 'People',
      spaces: 'Spaces',
    },
    aliasRequiredFriend: 'Sign into an Alias to send friend requests.',
    aliasRequiredJoin: 'Sign into an Alias to join Spaces.',
    actions: {
      viewProfile: 'View Profile',
    },
  },

  // Identity hover card / profile card actions
  identityCard: {
    viewProfile: 'Profile',
    message: 'Message',
    block: 'Block',
    unblock: 'Unblock',
    report: 'Report',
    removeFriend: 'Remove Friend',
  },

  // Friends
  friends: {
    title: 'Friends',
    noFriends: 'No friends yet.',
    searchPlaceholder: 'Search friends...',
    addFriend: 'Add Friend',
    removeFriend: 'Remove Friend',
    pending: 'Friend Request Sent',
    alreadyFriends: 'Friends',
    requestSent: 'Friend request sent.',
    requestError: 'Could not send friend request. Please try again.',
    requestAccepted: 'Friend request accepted.',
    requestIgnored: 'Friend request ignored.',
    friendRemoved: 'Friend removed.',
    incomingRequests: 'Friend Requests',
    accept: 'Accept',
    ignore: 'Ignore',
    cancel: 'Cancel Request',
    close: 'Close',
    searchingOnlyFriends: "You're searching only your friends.",
    searchEveryone: 'Search Everyone',
    viewProfile: 'View Profile',
    remove: 'Remove',
    friendsForDuration: 'Friends for {{duration}}',
    friendshipLengthUnknown: 'a while',
    friendshipLengthLessThanMinute: 'less than a minute',
    friendshipLengthMinutes_one: '1 minute',
    friendshipLengthMinutes_other: '{{count}} minutes',
    friendshipLengthHours_one: '1 hour',
    friendshipLengthHours_other: '{{count}} hours',
    friendshipLengthDays_one: '1 day',
    friendshipLengthDays_other: '{{count}} days',
    friendshipLengthMonths_one: '1 month',
    friendshipLengthMonths_other: '{{count}} months',
    friendshipLengthYears_one: '1 year',
    friendshipLengthYears_other: '{{count}} years',
    notifications: {
      requestReceived: 'Friend Request',
      requestReceivedBody: '{{name}} sent you a friend request.',
      requestAccepted: 'Friend Request Accepted',
      requestAcceptedBody: '{{name}} accepted your friend request.',
    },
  },

  // Notifications
  notifications: {
    title: 'Notifications',
    subtitle: 'Your notifications and alerts.',
    noNotifications: 'No notifications.',
    markAllRead: 'Mark all as read',
    clearAll: 'Clear all',
    clearHistory: 'Clear history',
    // Time formatting
    time: {
      justNow: 'Just now',
      minutesAgo: '{{count}}m ago',
      hoursAgo: '{{count}}h ago',
      daysAgo: '{{count}}d ago',
    },
    // Toast notifications
    toast: {
      newNotificationTitle: 'New Notification',
      newNotificationDescription: 'You have a new notification',
    },
    // Unified inbox panel (bell)
    inbox: {
      loadMore: 'Load more',
      empty: "You're all caught up.",
      // Row descriptions for non-message rows; message rows use `notifications.copy`.
      friendRequest: 'Sent you a friend request',
      friendAccepted: 'Accepted your friend request',
      dm: 'Sent you a message',
      call: 'Called you',
      groupInvite: 'Invited you to a group',
      spaceInvite: 'Invited you to a Space',
      achievement: 'Unlocked an achievement',
      generic: 'New notification',
    },
    // Message notification copy shared by toasts, OS notifications, and inbox rows.
    // Which details appear is controlled by the alias notification detail settings.
    copy: {
      someone: 'Someone',
      yourDm: 'your DM',
      yourGroup: 'your group',
      aChannel: 'a channel',
      channelRef: '#{{channel}}',
      titles: {
        message: 'New message',
        reply: 'New reply',
        mention: 'Mention',
        reaction: 'Reaction',
      },
      conversation: {
        message: '{{sender}} responded in {{where}}',
        reply: '{{sender}} replied to you in {{where}}',
        mention: '{{sender}} mentioned you in {{where}}',
        reaction: '{{sender}} reacted to your message in {{where}}',
      },
      space: {
        message: '{{sender}} posted in {{where}}',
        reply: '{{sender}} replied to you in {{where}}',
        mention: '{{sender}} mentioned you in {{where}}',
        mentionHere: '{{sender}} mentioned @here in {{where}}',
        mentionRole: '{{sender}} mentioned a role you have in {{where}}',
        reaction: '{{sender}} reacted to your message in {{where}}',
        reactionEmoji: '{{sender}} reacted {{emoji}} to your message in {{where}}',
      },
    },
  },

  // Blocked users
  blocked: {
    title: 'Blocked Aliases',
    subtitle: 'Manage aliases you have blocked.',
    noBlocked: 'No blocked aliases.',
    unblock: 'Unblock',
    blockUser: 'Block',
    confirmBlock: 'Are you sure you want to block this alias?',
    confirmUnblock: 'Are you sure you want to unblock this alias?',
    blocked: 'Blocked',
    userBlocked: 'Alias blocked',
    userUnblocked: 'Alias unblocked',
    blockedBanner: 'You have blocked this alias. You cannot send or receive messages.',
    blockedByOtherBanner: 'This alias has blocked you. You cannot send or receive messages.',
    blockUserAction: 'Block Alias',
  },

  // Spaces
  spaces: {
    deleteMessage: 'Delete',
    title: 'Spaces',
    spaceTitle: 'Space',
    subtitle: 'Discover and join public communities.',
    comingSoon: 'Spaces are on the way. Check back soon.',
    searchPlaceholder: 'Search Spaces',
    signInHeading: 'Sign into an Alias',
    signInBody: 'Sign into an Alias to join Spaces.',
    signInCta: 'Go to Aliases',
    guestPreview: {
      privateHeading: 'Private Space',
      privateBody: "This Space is private or doesn't exist. You need an invite to see it.",
      fullyEncrypted:
        "This Space is fully encrypted, so its name and description can't be shown.",
      paidPlan: 'Sign in and view this Space with an anonymous Alias!',
      signIn: 'Sign in',
      aliasBody: 'Sign into an Alias to view this Space.',
    },
    guestBanner: {
      label: 'Sign in',
      text: 'Sign in to see more channels and join the conversation.',
      aliasText: 'Sign into an Alias to see more channels and join the conversation.',
    },
    guestChannelGate: {
      heading: 'Sign in to view this channel',
      headingNamed: 'Sign in to view #{{name}}',
      voiceHeadingNamed: 'Sign in to join {{name}}',
      body: 'Channel messages and voice are only available once you sign in.',
      aliasBody: 'Channel messages and voice are only available once you sign into an Alias.',
    },
    loadMore: 'Load more',
    loadMoreError: "Couldn't load more Spaces.",
    join: 'Join',
    joining: 'Joining\u2026',
    joined: 'Joined',
    banned: 'Banned',
    bannedCannotJoin: 'You are banned from this Space',
    open: 'Open',
    encrypted: 'Encrypted',
    memberCount: '{{count}} member',
    memberCount_one: '{{count}} member',
    memberCount_other: '{{count}} members',
    lurking: 'Lurking',
    lurkSuccess: 'Now lurking {{name}}',
    lurkError: 'Could not update lurking.',
    contextMenu: {
      addFavorite: 'Add to Favourites',
      removeFavorite: 'Remove from Favourites',
      markAllRead: 'Mark all as read',
      leave: 'Leave Space',
      stopLurking: 'Stop lurking',
    },
    leaveSpace: {
      title: 'Leave Space?',
      confirm: 'You will need a new invite to rejoin this Space.',
      confirmBtn: 'Leave',
      error: 'Could not leave Space.',
    },
    favoriteAddedToast: 'Added to Favourites',
    favoriteRemovedToast: 'Removed from Favourites',
    notifications: {
      bannedTitle: 'Banned from Space',
      bannedBody: 'You were banned from {{name}}',
      bannedBodyGeneric: 'You were banned from a Space',
      kickedTitle: 'Removed from Space',
      kickedBody: 'You were removed from {{name}}',
      kickedBodyGeneric: 'You were removed from a Space',
      temporaryExpiredTitle: 'Temporary access expired',
      temporaryExpiredBody: 'Your temporary access to {{name}} has expired',
      temporaryExpiredBodyGeneric: 'Your temporary access to this Space has expired',
      inviteTitle: 'Space invitation',
      inviteBody: "You've been invited to {{name}}",
      inviteBodyGeneric: "You've been invited to a Space",
      announcementTitle: 'Announcement',
      announcementBody: '{{name}}: {{text}}',
      announcementBodyGeneric: 'New announcement in {{name}}',
      announcementBodyGenericNoName: 'New announcement in a Space',
      someSpace: 'A Space',
      spaceChangedTitle: 'Space updated',
      spaceChangedBody: '{{name}}: {{changes}} changed',
      spaceChangedBodyGeneric: 'A Space you are in changed its {{changes}}',
      channelEncryptionChangedBody: '{{name}}: encryption settings changed for #{{channel}}',
      channelEncryptionChangedBodyGeneric: '{{name}}: encryption settings changed for a channel',
      categoryEncryptionChangedBody: '{{name}}: encryption settings changed for {{category}}',
      categoryEncryptionChangedBodyGeneric: '{{name}}: encryption settings changed for a category',
      changeKinds: {
        name: 'name',
        description: 'description',
        logo: 'icon',
        banner: 'banner',
        theme: 'theme',
        visibility: 'visibility',
        access: 'membership access',
        encryption: 'encryption settings',
      },
    },
    encryptedSpacePlaceholder: 'Encrypted Space',
    encryptedChannelPlaceholder: 'Encrypted channel',
    encryptedRolePlaceholder: 'Encrypted role',
    visibility: {
      public: 'Public',
      unlisted: 'Unlisted',
      hidden: 'Hidden',
    },
    joinModal: {
      rulesTitle: 'Rules',
      rulesPlaceholder: 'Rules coming soon.',
      cipherTitle: 'Community Cipher',
      cipherRequiredBadge: 'Cipher required',
      cipherChecking: 'Checking your Ciphers\u2026',
      cipherMatched: 'You have a matching Cipher for this Space.',
      cipherMissingRequired: 'None of your Ciphers match this Space. Add the matching Cipher to join.',
      cipherMissingOptional: 'None of your Ciphers match this Space. You can still join, but you will not be able to read encrypted messages.',
      cipherUnavailableRequired: 'Unlock your Alias and add the matching Cipher to join.',
      cipherUnavailableOptional: 'Unlock your Alias to check for a matching Cipher. You can still join without one.',
      checkCiphers: 'Check against my current Ciphers',
      addCipher: 'Add a Cipher',
      confirmAddCipher: 'Use this Cipher',
      addingCipher: 'Verifying\u2026',
      cipherMismatch: 'That Cipher does not match this Space.',
      cipherJoinDenied: 'You need the matching Community Cipher to join this Space.',
      browse: 'Browse',
      cancel: 'Cancel',
      lurk: 'Lurk',
      stopLurking: 'Stop lurking',
      freeUsersBlocked:
        'This Space has opted to prevent free users from posting to reduce spam and bots',
      paidPlanRequired: 'A paid plan is required to join this Space.',
    },
    create: {
      cta: 'Create Space',
      title: 'Create a Space',
      subtitle: 'Spin up a community with channels and members.',
      nameLabel: 'Name',
      namePlaceholder: 'My Community',
      nameHint: 'Shown at the top of your Space.',
      slugLabel: 'URL',
      slugHint: 'Lives at /s/your-url \u2014 lowercase letters, numbers, and hyphens.',
      slugChecking: 'Checking availability\u2026',
      slugAvailable: 'Available',
      slugTaken: 'That URL is taken',
      slugInvalid: 'URLs must be 3\u201332 lowercase letters, numbers, and internal hyphens.',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'What is this Space about?',
      optional: 'Optional',
      visibilityLabel: 'Visibility',
      visibilityPublicDesc:
        'Anyone can find and join. The only type that appears in the Spaces directory. Best for most communities and interests.',
      visibilityUnlistedDesc:
        'Never appears in the directory or search, but anyone with the URL or an invite can visit and join. Best for communities that want to stay accessible, but not show up on Google.',
      visibilityHiddenDesc:
        'Invite-only and hidden from search. No custom URL — members reach it only via invite or Cipher.',
      allowFreeMembersLabel: 'Allow free members',
      allowFreeMembersHint: 'Let free-tier Aliases join and post.',
      stepAbout: 'About',
      stepVisibility: 'Visibility',
      stepEncryption: 'E2E Encryption',
      next: 'Next',
      back: 'Back',
      hiddenNoSlugHint:
        'Hidden Spaces do not get a custom URL. The app uses an opaque id in the address bar when needed.',
      encryptionLabel: 'End-to-end encryption',
      encryptionToggle: 'E2E Encrypt all metadata with a Cipher',
      encryptionHint:
        "By default, all content (including channels, roles, messages and other metadata) is encrypted with a Cipher. If you decide you don't want this, you can change this later Space-wide or for individual channels, categories, etc.",
      encryptIdentityToggle: 'Also E2E encrypt Space name and description',
      encryptIdentityHint:
        'Hides the name and description from anyone without the Cipher, including people who reach the Space by URL.',
      encryptIdentityUnlistedUrlNote:
        'Space URL is (by its nature) always cleartext. Be careful to avoid leaking information via URL, or use the Hidden Space type to skip the custom URL entirely.',
      encryptionPublicNote: 'Public Spaces can not use Space-wide Ciphers (due to them being public). You will still be able to add Ciphers to individual categories or channels to E2E encrypt messages, but general Space metadata remains public.',
      encryptionUnavailable: 'Unlock your Alias to create an encrypted Space.',
      cipherRequiredToggle: 'Require a Cipher to join',
      cipherRequiredHint:
        'Joining requires a matching Cipher. The Cipher itself is never sent to the server. Content encryption is optional.',
      cipherRequiredPublicNote:
        "Public Spaces cannot require a Cipher to join — that would make them not public (gasp!). Choose Unlisted or Hidden to gate membership with a Cipher.",
      cipherRequiredNeedsCipher: 'Select or create a Cipher to gate entry.',
      cipherSourceExisting: 'Use an existing Cipher',
      cipherSourceNew: 'Create a new Cipher',
      cipherSelectLabel: 'Cipher',
      cipherSelectPlaceholder: 'Select a Cipher',
      cipherSearchPlaceholder: 'Search or select other Ciphers\u2026',
      cipherSelectedBadge: 'Selected',
      cipherNoneSelected: 'No Cipher selected yet.',
      cipherClearSelection: 'Clear Cipher selection',
      cipherNoMatch: 'No Ciphers match your search.',
      noCiphers: "You don't have any Ciphers yet \u2014 create one instead.",
      newCipherNameLabel: 'Cipher name',
      newCipherNamePlaceholder: 'My Space key',
      entropyLabel: 'Secret phrases',
      entropyHint: 'Anyone with these phrases can unlock this Space. Store them safely.',
      entropyPlaceholder: 'Enter a secret phrase',
      addPhrase: 'Add phrase',
      removePhrase: 'Remove phrase',
      submit: 'Create Space',
      submitting: 'Creating\u2026',
      cancel: 'Cancel',
      errors: {
        nameRequired: 'Enter a name for your Space.',
        slugUnavailable: 'Choose an available URL.',
        cipherRequired: 'Select or create a Cipher for this Space.',
        entropyRequired: 'Add at least one secret phrase.',
        tierRequired: 'Upgrade to a paid plan to create a Space.',
        creationDisabled: 'Creating new Spaces is currently unavailable.',
        createFailed: "Couldn't create the Space. Please try again.",
      },
      disabled: {
        heading: 'Space creation unavailable',
        body: 'Creating new Spaces is currently unavailable. Check back later.',
        back: 'Back to Spaces',
      },
    },
    empty: {
      heading: 'No Spaces yet',
      body: 'There are no public Spaces to show right now. Check back soon.',
    },
    error: {
      heading: "Couldn't load Spaces",
      body: 'Something went wrong while loading Spaces. Please try again.',
      retry: 'Try again',
    },
    joinSuccess: 'Joined {{name}}.',
    joinError: "Couldn't join this Space.",
    invites: {
      modalTitle: 'Invite to Space',
      modalSubtitle: 'Invite people to {{name}}.',
      cipherJoinBlurb: 'Invited users will still need to know the Cipher to join.',
      optionsSection: 'Invite Options',
      advancedSection: 'Advanced Options',
      listSection: 'Invite Friends',
      tabs: {
        people: 'People',
        link: 'Link',
      },
      searchFriendsPlaceholder: 'Search your friends',
      searchAnyonePlaceholder: 'Search by username',
      searchToBegin: 'Search for someone to invite.',
      noFriends: 'No friends to invite yet.',
      noMatches: 'No matches.',
      noPermission: "You don't have permission to invite people to this Space.",
      invite: 'Invite',
      statusInvited: 'Invited',
      statusInvitedBy: 'Invited by {{name}} (@{{username}})',
      statusInvitedByOther: 'Invited by someone else',
      sent: 'Invite sent.',
      createLink: 'Create invite link',
      createAnother: 'Create another',
      removeLink: 'Remove Link',
      linkLabel: 'Invite link',
      linkCreatedHint: 'Anyone with this link can join until {{when}}.',
      linkCreatedHintNever: 'Anyone with this link can join. It does not expire.',
      linkScopeHint:
        'The link grants Space membership and the default role. Extra roles and additional channels are optional. It never carries a Cipher.',
      hiddenWarning: 'This Space is hidden: long-lived links generally make things less hidden, so be mindful.',
      copy: 'Copy link',
      copied: 'Link copied.',
      expiresLabel: 'Invitation Expiration',
      expiresHint: 'How long this invite stays usable. After it expires, it can no longer be accepted.',
      maxUsesLabel: 'Max uses',
      maxUsesHint:
        'How many times this link can be used. No limit keeps it usable until it expires.',
      maxUses: {
        unlimited: 'No limit',
        count: '{{count}} use',
        count_one: '{{count}} use',
        count_other: '{{count}} uses',
        custom: 'Custom',
        customLabel: 'Custom number of uses',
        customPlaceholder: 'Number of uses',
        customHint: 'Enter a whole number from 1 to {{max}}.',
        customInvalid: 'Enter a whole number from 1 to {{max}}.',
      },
      temporaryLabel: 'Membership Expiration',
      temporaryHint: 'Temporary members are removed automatically when their access runs out.',
      temporary: {
        permanent: 'Never',
      },
      rolesLabel: 'Assigned Roles',
      rolesHint:
        'Everyone is always assigned. You can add any other roles you are allowed to grant. Granting roles with permissions you do not hold requires {{manageRoles}}.',
      channelsLabel: 'Additional Channels',
      channelsHint:
        'Optional. Invitees still join the Space and see Everyone-open channels. Additional channels get a generated role so invitees can see them too. Requires {{manageRoles}} and {{manageChannels}}.',
      channelsSearchPlaceholder: 'Search channels',
      channelsRemove: 'Remove {{name}}',
      channelsLoading: 'Loading channels…',
      channelsNoneSelected: 'No channels selected',
      channelsEmpty: 'No additional channels you can add.',
      channelsNoMatch: 'No matching channels.',
      fieldHintAria: 'About {{label}}',
      quotaLabel: '{{used}} / {{limit}} direct invites in the last 24 hours',
      quotaHint:
        'To help prevent invite spam, a Space can send direct invites to at most 5% of its members in a rolling 24 hours, with a minimum of 10 and a maximum of 100. This Space currently has {{memberCount}} members, so the limit is {{limit}} per 24 hours. Links have no such limits.',
      quotaReached: 'This Space has reached its direct-invite limit for the last 24 hours.',
      duration: {
        '30m': '30 minutes',
        '1h': '1 hour',
        '6h': '6 hours',
        '12h': '12 hours',
        '1d': '1 day',
        '7d': '7 days',
        '30d': '30 days',
        never: 'Never',
      },
      errors: {
        inviteFailed: "Couldn't send that invite.",
        targetCooldown:
          'This person was already invited twice in the last 24 hours. Change their roles, channels, or temporary access to send an updated invite, or wait.',
        quota: 'This Space has reached its direct-invite limit for the last 24 hours.',
        linkFailed: "Couldn't create an invite link.",
        removeFailed: "Couldn't remove that invite link.",
        copyFailed: "Couldn't copy the link.",
      },
      inbox: {
        spaceInvite: 'Space Invitation',
        typeSpace: 'Space',
        invitedBy: 'Invited by {{name}}',
        includesAccess: 'Includes access to {{roles}} and {{channels}}',
        includesAccessRoles: 'Includes access to {{count}} roles',
        includesAccessRoles_one: 'Includes access to 1 role',
        includesAccessRoles_other: 'Includes access to {{count}} roles',
        includesAccessChannels: 'Includes access to {{count}} channels',
        includesAccessChannels_one: 'Includes access to 1 channel',
        includesAccessChannels_other: 'Includes access to {{count}} channels',
        roleCount: '{{count}} roles',
        roleCount_one: '1 role',
        roleCount_other: '{{count}} roles',
        channelCount: '{{count}} channels',
        channelCount_one: '1 channel',
        channelCount_other: '{{count}} channels',
        inviteExpires: 'Invite expires in {{when}}',
        inviteExpired: 'Invite expired',
        expiresIn: {
          d: '{{count}}d',
          h: '{{count}}h',
          m: '{{count}}m',
        },
        temporaryMembership: 'Temporary membership ({{duration}})',
        moreMembers: '+{{count}} more',
        moreMembers_one: '+1 more',
        moreMembers_other: '+{{count}} more',
      },
      landing: {
        title: "You've been invited",
        subtitle: 'Join {{name}} on adieuu.',
        subtitleHidden: 'Join a hidden Space on adieuu.',
        signInToJoin: 'Sign in to join',
        cipherRequiredLead: 'This Space requires a',
        cipherTerm: 'Community Cipher',
        cipherRequiredHint:
          "A Community Cipher is a secret someone from the Space shares with you. It's used to encrypt messages end-to-end so Adieuu can't read them. This invite does not include a Cipher, so you will need someone from the Space to share one with you separately before you can join.",
        visibilityHint: {
          public: 'Anyone can find this Space in the directory and join.',
          unlisted:
            'This Space does not appear in the directory or in search. You need an invite or the URL to find it.',
          hidden:
            'This Space is invite-only. It is hidden from search and has no public URL.',
        },
        encryptedHint:
          'Messages and other content in this Space are end-to-end encrypted. Only people with the matching Community Cipher can read them.',
        encryptedIdentityHint:
          'This Space encrypts its name, description, and content. Without the Community Cipher you cannot see what is inside.',
        inviteExpires: 'This invite expires {{when}}.',
        temporaryMembership: 'Joining grants temporary access for {{duration}}.',
        invalid: 'This invite is no longer valid',
        subtitleInvalid: 'Ask whoever shared it for a new invite.',
        joined: 'Joined. Taking you there\u2026',
        decline: 'Not now',
        browseSpaces: 'Browse Spaces',
        addNewCipher: 'Add New Cipher',
        cipherMatched: 'You have a matching Cipher and may join this Space.',
      },
    },
    home: {
      editName: 'Edit Space name',
      editDescription: 'Edit Space description',
      namePlaceholder: 'Space name',
      descriptionPlaceholder: 'Tell people what this Space is about',
      addDescription: 'Add a description',
      nameRequired: 'A Space needs a name.',
      charCount: '{{count}}/{{max}}',
      uploadLogo: 'Upload Space logo',
      changeLogo: 'Change Space logo',
      removeLogo: 'Remove Space logo',
      uploadBanner: 'Upload Space banner',
      changeBanner: 'Change Space banner',
      removeBanner: 'Remove Space banner',
      bannerAlt: '{{name}} banner',
      editTheme: 'Edit theme',
      editingTheme: 'Editing theme',
      unsavedChanges: 'You have unsaved changes to this Space.',
      discard: 'Discard',
      save: 'Save changes',
      saving: 'Saving…',
      saved: 'Space updated.',
      saveFailed: "Couldn't save: {{error}}",
      permissionLost: 'Your permissions in this Space changed, so editing is no longer available.',
      cipherRequired: "Unlock this Space's Cipher to edit its profile.",
      encryptedMediaNote: 'Images are end-to-end encrypted with this Space’s Cipher.',
      tabs: {
        activity: 'Activity',
        announcements: 'Announcements',
        threads: 'Threads',
        activityPlaceholder: 'Space activity will show up here soon.',
        threadsPlaceholder: 'Threads from across this Space will show up here soon.',
        announcementsPlaceholder: 'Announcements are coming soon.',
      },
    },
    activity: {
      toolbarLabel: 'Activity options',
      sortLabel: 'Sort by',
      sorts: {
        new: 'New',
        hot: 'Hot',
        active: 'Active',
        top: 'Top',
      },
      sortHints: {
        new: 'Most recently posted first.',
        hot: 'Popular right now. Newer items with engagement rise above older ones.',
        active: 'Most recent reply or reaction first.',
        top: 'Most engagement first, within the time range you pick.',
      },
      sortHow: {
        new: 'Ordered by when each item was posted.',
        hot: 'Ordered by points and posting time together: 10× the points ranks the same as being posted {{hours}} hours later.',
        active: 'Ordered by the latest reaction or reply from someone other than the author.',
        top: 'Ordered by points, highest first.',
      },
      pointsFormula: 'Points in this Space: {{parts}}.',
      points: {
        reaction: '{{n}} per reaction',
        inlineReply: '{{n}} per reply',
        threadReply: '{{n}} per thread reply',
        ephemeralReply: '{{n}} per disappearing reply',
        pinBonus: '+{{n}} while pinned',
        announcement: '{{n}} for an announcement',
      },
      minScoreNote: 'Only items with at least {{min}} points appear. The author’s own reactions and replies don’t count.',
      info: {
        label: 'How Activity is Ranked',
        managers: {
          unknown: 'This Space’s admins can adjust these values.',
          one: '@{{a}} role can adjust these values.',
          two: '@{{a}} and @{{b}} roles can adjust these values.',
          three: '@{{a}}, @{{b}} and @{{c}} roles can adjust these values.',
          more: '@{{a}}, @{{b}} + {{n}} other roles can adjust these values.',
        },
        override: 'Your own sort and filters override the default view.',
        defaultView: 'Default view',
        defaultViewTop: '{{sort}}, {{window}}',
        pointsTitle: 'Points',
        manage: 'Change in Manage › Activity',
      },
      windowLabel: 'Time range',
      windowHint: 'Only count items posted within this time range.',
      windows: {
        '24h': 'Past 24 hours',
        '7d': 'Past week',
        '30d': 'Past month',
        all: 'All time',
      },
      filters: {
        title: 'Filters',
        labelHidden: 'Filters, {{count}} kinds hidden',
        labelHidden_one: 'Filters, 1 kind hidden',
        labelHidden_other: 'Filters, {{count}} kinds hidden',
        heading: 'Show in the feed',
        announcements: {
          label: 'Announcements',
          hint: 'Posts from this Space’s Announcements tab.',
        },
        pins: {
          label: 'Pinned messages',
          hint: 'Messages pinned in channels you can read.',
        },
      },
      resetToDefault: 'Reset to Space default',
      resetToDefaultHint: 'Reset to Space default. Clears the sort and filters you picked here.',
      saveFailed: "Couldn't save your activity view.",
      newActivity: 'New activity',
      empty: 'Nothing here yet. Messages with reactions and replies show up here.',
      loadError: "Couldn't load activity.",
      retry: 'Try again',
      loadMore: 'Load more',
      inChannel: 'in #{{channel}}',
      inAnnouncements: 'in Announcements',
      jumpToMessage: 'Jump to message',
      jumpToPin: 'Jump to pin',
      jumpToAnnouncement: 'Jump to announcement',
      unknownChannel: 'Channel',
      deleteFailed: 'Failed to delete message.',
    },
    announcements: {
      empty: 'No announcements yet.',
      emptyManager: 'No announcements yet. Post the first one above.',
      emptyPublic: 'No public announcements yet.',
      loadError: "Couldn't load announcements.",
      loadMore: 'Load more',
      retry: 'Try again',
      newCta: 'New announcement',
      titleLabel: 'Title (optional)',
      titlePlaceholder: 'Add a title',
      bodyLabel: 'Announcement',
      composerLabel: 'Announcement composer',
      bodyPlaceholder: 'Write an announcement…',
      imagesOnly: 'Only images can be added to announcements.',
      mediaLimit: 'Announcements can include up to {{max}} images.',
      gifLimit: 'Announcements can include up to {{max}} GIFs.',
      removeGif: 'Remove GIF',
      write: 'Write',
      preview: 'Preview',
      previewEmpty: 'Nothing to preview yet.',
      post: 'Post',
      posting: 'Posting…',
      save: 'Save',
      saving: 'Saving…',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      edited: 'edited',
      editedBy: 'Edited by {{name}}',
      formerMember: 'Former member',
      postShortcut: 'Ctrl+Enter to post',
      postShortcutMac: '⌘+Enter to post',
      textTooLong: 'Announcements can be up to {{max}} characters.',
      titleTooLong: 'Titles can be up to {{max}} characters.',
      postError: "Couldn't post the announcement.",
      editError: "Couldn't save your changes.",
      deleteError: "Couldn't delete the announcement.",
      conflict: 'Someone else edited this announcement. Reload it to see their changes before saving yours.',
      reload: 'Reload',
      discardTitle: 'Discard changes?',
      discardBody: 'Your unsaved announcement will be lost.',
      discardConfirm: 'Discard',
      deleteConfirmTitle: 'Delete this announcement?',
      deleteConfirmBody: 'This removes it for everyone and cannot be undone.',
      locked: "Unlock this Space's Cipher to read announcements.",
      decryptFailed: "This announcement couldn't be decrypted.",
      unverified: "This announcement's signature couldn't be verified.",
      notFound: 'That announcement is no longer available.',
      audience: {
        label: 'Visible to',
        everyone: 'Everyone',
        channels: 'Channels',
        roles: 'Roles',
        channelsPlaceholder: 'Any channel',
        rolesPlaceholder: 'Any role',
        matchLabel: 'Members must match',
        matchAll: 'Channel and role',
        matchAny: 'Channel or role',
        deletedChannel: 'Deleted channel',
        deletedRole: 'Deleted role',
        unknownChannel: 'Unavailable channel',
        channelsHint: 'Only members who can see one of these channels. Leave empty for any channel.',
        rolesHint: 'Only members who have one of these roles. Leave empty for any role.',
        matchHint: 'Choose whether members need both a listed channel and a listed role, or just one of them.',
        remove: 'Remove {{name}}',
        and: 'and',
        or: 'or',
        tooManyChannels: 'Pick up to {{max}} channels.',
        tooManyRoles: 'Pick up to {{max}} roles.',
        summaryEveryone: 'This announcement will be visible to everyone in this Space.',
        summaryChannels: 'This announcement will be visible to users that can see {{channels}}.',
        summaryRoles: 'This announcement will be visible to users that have {{roles}}.',
        summaryAll: 'This announcement will be visible to users that can see {{channels}} and have {{roles}}.',
        summaryAny: 'This announcement will be visible to users that can see {{channels}} or have {{roles}}.',
        channelPhrase_one: 'the {{list}} channel',
        channelPhrase_other: 'the {{list}} channels',
        rolePhrase_one: 'the {{list}} role',
        rolePhrase_other: 'the {{list}} roles',
        publicWarning: 'Because this Space is public, anyone can read it, including people who are not members.',
        publicWarningUnlisted: 'Because this Space is unlisted, anyone with its link can read it, including people who are not members.',
        managerNote: 'Members who can manage announcements always see every announcement here.',
        toastNoteChannels: 'Toasts appear only in {{channels}}.',
        toastNoteRoleOnly: 'Members who only match by role see the toast in any channel.',
        infoAria: 'About who can see this announcement',
        edit: 'Change who can see this announcement',
      },
      toast: {
        region: 'Space announcements',
        label: 'Announcement',
        learnMore: 'Learn more',
        dismiss: 'Dismiss announcement',
        more_one: '+{{count}} more',
        more_other: '+{{count}} more',
        dismissError: "Couldn't dismiss the announcement. Try again.",
      },
    },
    themePanel: {
      title: 'Space theme',
      regionLabel: 'Space theme editor',
      close: 'Close theme editor',
      collapse: 'Collapse theme editor',
      expand: 'Expand theme editor',
      startFrom: 'Start from',
      startFromPlaceholder: 'Search themes…',
      startFromNoResults: 'No themes match.',
      builtinGroup: 'Built-in themes',
      customGroup: 'Your themes',
      groups: {
        surfaces: 'Surfaces',
        text: 'Text',
        accents: 'Accents',
      },
      tokens: {
        background: 'Background',
        sidebar: 'Sidebar',
        card: 'Cards',
        elevated: 'Menus & popovers',
        text: 'Text',
        textMuted: 'Secondary text',
        accent: 'Accent',
        accentText: 'Text on accent',
        link: 'Links',
        border: 'Borders',
      },
      hexLabel: '{{token}} hex value',
      invalidHex: 'Use a 6-digit hex color like #1a2b3c.',
      unset: 'Uses the viewer’s theme',
      pickerLabel: '{{token}} color',
      pickerClear: 'Clear {{token}}',
      pickerSave: 'Save color',
      pickerPrevious: 'Previous color',
      contrastLow: 'Low contrast ({{ratio}}:1) between {{a}} and {{b}}.',
      contrastWhy:
        'Contrast is how much text stands out from the color behind it. When it’s low, text is hard to read for people with low vision or color blindness, on dim or low-quality screens, and in bright sunlight.',
      contrastWhyTip:
        'Accessibility guidelines (WCAG) recommend at least {{min}}:1 for regular text. Try a lighter or darker shade of one color until this notice goes away. Your whole Space will be easier to read.',
      reset: 'Reset',
      resetHint: 'Clear every color so members see their own theme.',
      discard: 'Discard',
      save: 'Save theme',
      saving: 'Saving…',
      saved: 'Space theme saved.',
      saveFailed: "Couldn't save the theme: {{error}}",
      confirmClose: 'Discard your unsaved theme changes?',
      keepEditing: 'Keep editing',
      discardChanges: 'Discard changes',
      permissionLost: 'You no longer have permission to edit this Space’s theme.',
      cipherRequired: "Unlock this Space's Cipher to edit its theme.",
      previewNote: 'You are previewing the Space theme. Members who use their own theme will not see it.',
    },
    themeToggle: {
      label: 'Use Space theme',
      showingSpace: 'Showing this Space’s theme. Select to use your theme here.',
      showingOwn: 'Showing your theme. Select to use this Space’s theme here.',
      saveFailed: 'Could not save your theme choice for this Space.',
    },
    themeSettings: {
      title: 'Space themes',
      description:
        'Spaces can set their own colors for their channels and home. Choose what you see by default. You can change it for any Space with the palette button next to its name.',
      legend: 'By default',
      useSpace: 'Use Space themes',
      useSpaceHint: 'See each Space the way it was designed.',
      useOwn: 'Use my theme',
      useOwnHint: 'Keep your own colors everywhere.',
      changedHint:
        'Spaces where you picked differently keep that choice. Use “Apply to all Spaces” to use this setting everywhere.',
      applyAll: 'Apply to all Spaces',
      applyAllHint: 'Clear the choices you made for individual Spaces.',
      confirmTitle: 'Apply to all Spaces?',
      confirmChecking: 'Checking your Spaces…',
      confirmCount_one:
        'You picked a theme setting for {{count}} Space. It will follow your default ({{setting}}) instead.',
      confirmCount_other:
        'You picked a theme setting for {{count}} Spaces. They will follow your default ({{setting}}) instead.',
      confirmNone: 'Every Space already follows your default ({{setting}}).',
      confirmFailed: 'Could not check your Spaces. You can still apply your default to all of them.',
      confirm: 'Apply to all',
      cancel: 'Cancel',
      applied: 'Every Space now follows your default.',
      applyFailed: 'Could not apply your default to all Spaces. Please try again.',
    },
    logo: {
      alt: '{{name}} logo',
      locked: "Unlock this Space's Cipher to see its logo",
    },
    view: {
      notFoundHeading: 'Space not found',
      notFoundBody: "This Space doesn't exist or isn't available to you.",
      back: 'Back to Spaces',
      errorHeading: "Couldn't load this Space",
      errorBody: 'Something went wrong while loading this Space. Please try again.',
      retry: 'Try again',
    },
    sidebar: {
      home: 'Home',
      manage: 'Manage',
      inviteToSpace: 'Invite to Space',
      spaceMenu: 'Space options',
      textChannels: 'Text Channels',
      createChannel: 'Create channel',
      editChannel: 'Edit channel',
      createCategory: 'Create category',
      createChildCategory: 'Create child category',
      editCategory: 'Edit category',
      deleteCategory: 'Delete category',
      newCategoryName: 'New Category',
      layoutError: "Couldn't update channel order.",
      createCategoryError: "Couldn't create category.",
      openMenu: 'Open Space menu',
      closeMenu: 'Close Space menu',
      selectChannel: 'Select a channel',
      unreadBadge: '{{count}} unread',
      mentionBadge: '{{count}} mentions',
      mentionOnlyBadge: 'You were mentioned',
      viewTextChat: 'View Text Chat',
      resizeSidebar: 'Drag to resize Space sidebar',
    },
    createCategory: {
      title: 'Create category',
      nameLabel: 'Category name',
      namePlaceholder: 'Category',
      encryptLabel: 'Encrypt category channels',
      encryptHint:
        'New channels in this category encrypt messages with a Cipher on your device by default.',
      encryptSpaceE2eeHint:
        'Encrypted Spaces always encrypt channels in this category. Choose a default Cipher; encryption cannot be turned off.',
      create: 'Create',
      creating: 'Creating…',
      error: "Couldn't create category.",
      forceChildrenLabel: 'Force children to use these settings',
      forceChildrenHint:
        'Choose which settings child categories and channels must follow. Forced settings cannot be changed on children.',
      forceChildrenAcl: 'Force roles / permissions',
      forceChildrenCipher: 'Force encryption',
    },
    editCategory: {
      title: 'Edit category',
      save: 'Save',
      saving: 'Saving…',
      error: "Couldn't update category.",
      deleteError: "Couldn't delete category.",
      cipherProofRequired: 'You need the matching Community Cipher to change encryption.',
    },
    createChannel: {
      title: 'Create channel',
      nameLabel: 'Channel name',
      namePlaceholder: 'new-channel',
      typeLabel: 'Channel type',
      typeText: 'Text',
      typeVoice: 'Audio/Video',
      rolesLabel: 'Restrict to roles',
      rolesHint: 'Defaults to Everyone if none selected.',
      rolesSearchPlaceholder: 'Search roles…',
      rolesNoneSelected: 'No roles selected',
      rolesLoading: 'Loading roles…',
      rolesEmpty: 'No roles available',
      rolesNoMatch: 'No roles match your search',
      rolesRemove: 'Remove {{name}}',
      inheritRolesLabel: 'Inherit roles from parent',
      inheritRolesHint: 'Use the parent category’s roles (or Everyone at the Space root).',
      inheritRolesForced:
        'Roles are forced by {{name}} and cannot be changed here.',
      inheritEncryptionLabel: 'Inherit encryption from parent',
      inheritEncryptionHint:
        'Use the parent category’s encryption settings (or the Space when at the root).',
      inheritEncryptionForced:
        'Encryption is forced by {{name}} and cannot be changed here.',
      inheritEncryptionPreviewOff: 'Not encrypted',
      inheritEncryptionPreviewOn: 'Encrypted',
      inheritEncryptionPreviewCipher: 'Cipher: {{name}}',
      inheritEncryptionPreviewUnknownCipher: 'Encrypted with parent Cipher',
      encryptLabel: 'Encrypt channel',
      encryptHint: 'Encrypt messages in this channel with a Cipher on your device.',
      encryptSpaceE2eeHint:
        'Encrypted Spaces always encrypt this channel. Choose a Cipher; encryption cannot be turned off.',
      create: 'Create',
      cancel: 'Cancel',
      creating: 'Creating…',
      error: "Couldn't create channel.",
    },
    editChannel: {
      title: 'Edit channel',
      save: 'Save',
      saving: 'Saving…',
      error: "Couldn't update channel.",
      cipherChangeTitle: 'Change channel Cipher?',
      cipherChangeWarning:
        'Anyone without this Cipher will be unable to read new messages in this channel. Existing messages stay encrypted with the previous Cipher (if any).',
      cipherChangeConfirm: 'Continue',
      cipherProofRequired: 'You need the matching Community Cipher to change encryption.',
    },
    permissionOverrides: {
      open: 'Channel-Specific Permissions',
      openHint: 'Override what each role can do in this channel or category.',
      title: 'Permissions — {{name}}',
      description:
        'Optional full overrides for this location. Unset roles use the Space role (and parent category overrides).',
      loading: 'Loading permissions…',
      noRoles: 'No roles available to configure.',
      selectRole: 'Select a role to edit permissions.',
      customBadge: 'Custom',
      statusCustom: 'Custom permissions for this location.',
      statusInherited: 'Using Space / parent permissions. Save to create an override.',
      remove: 'Remove overrides',
      removeAll: 'Remove all overrides',
      removeAllTitle: 'Remove all permission overrides?',
      removeAllDescription:
        'Every role will use Space / parent permissions again for this location. This cannot be undone from here.',
      removeAllConfirm: 'Remove all',
      removed: 'Overrides removed.',
      saved: 'Permissions saved.',
      saveError: "Couldn't save permission overrides.",
    },
    manage: {
      title: 'Manage Space',
      subtitle: 'Overview and settings for {{name}}',
      navLabel: 'Space management',
      nav: {
        overview: 'Overview',
        members: 'Members',
        roles: 'Roles',
        invites: 'Invites',
        activity: 'Activity',
        audit: 'Audit log',
        backToSpace: 'Back to Space',
      },
      members: spaceManageMembers,
      activity: {
        title: 'Activity',
        subtitle: 'Choose how the Space Home activity feed opens and what ranks a message higher.',
        disabled: 'The activity feed is not available on this server yet.',
        loadError: "Couldn't load activity settings.",
        rescoring: 'Re-ranking existing activity with the new weights. This can take a few minutes.',
        defaultsTitle: 'Default view',
        defaultsHint: 'Members who have not picked their own view see this. Their own choice always wins.',
        defaultSort: 'Sort',
        defaultSortHint: 'How the feed is ordered when it opens.',
        defaultTopWindow: 'Top time range',
        defaultTopWindowHint: 'How far back Top looks until a member picks their own range.',
        weightsTitle: 'Ranking weights',
        weightsHint:
          'Points a message earns for each kind of engagement from other members. A message needs {{min}} points to appear. Each weight is a whole number from 0 to {{max}}.',
        weights: {
          reaction: { label: 'Reaction', hint: 'Per member who reacts' },
          inlineReply: { label: 'Reply', hint: 'Per reply that quotes it' },
          threadReply: { label: 'Thread reply', hint: 'Per reply in its thread' },
          ephemeralReply: { label: 'Disappearing reply', hint: 'Per disappearing reply' },
          pinBonus: { label: 'Pinned', hint: 'Added while it is pinned' },
          announcement: { label: 'Announcement', hint: 'Score of every announcement' },
        },
        invalidWeights: 'Every weight must be a whole number from 0 to {{max}}.',
        weightChangesLeft:
          'Changing a weight re-ranks all of this Space’s activity, so it is limited to {{perDay}} per 24 hours. {{left}} left.',
        weightsLock: {
          rescoring:
            'Weights are locked while existing activity is re-ranked, and for {{minutes}} minutes after it finishes.',
          cooldown: 'Activity was just re-ranked. You can change the weights again at {{time}}.',
          dailyLimit:
            'This Space has used its {{perDay}} weight changes for the last 24 hours. You can change them again at {{time}}.',
        },
        examplesTitle: 'Example scores',
        examplesHint: 'How these weights score some typical items. Anything under {{min}} stays out of the feed.',
        examplesItem: 'Item',
        examplesScore: 'Score',
        examplesShown: 'In feed',
        examplesYes: 'Yes',
        examplesNo: 'No',
        examples: {
          oneReaction: 'A message with 1 reaction',
          threeReactions: 'A message with 3 reactions',
          twoReplies: 'A message with 2 replies',
          busyThread: 'A thread with 4 replies and 2 reactions',
          disappearing: 'A message with 2 disappearing replies',
          pinned: 'A pinned message with no engagement',
          announcement: 'An announcement',
        },
        save: 'Save',
        saving: 'Saving…',
        saved: 'Activity settings saved.',
        saveError: "Couldn't save activity settings.",
        discard: 'Discard changes',
        resetDefaults: 'Reset to defaults',
      },
      loadError: "Couldn't load Space management data.",
      stats: {
        members: 'Members',
        channels: 'Channels',
        visibility: 'Visibility',
        created: 'Created',
      },
      recentJoins: {
        title: 'Recent joins',
        empty: 'No recent joins yet.',
      },
      banned: {
        title: 'Banned members',
        empty: 'No banned members.',
        unban: 'Unban',
        unbanSuccess: 'Member unbanned.',
        unbanError: "Couldn't unban that member.",
      },
      invites: {
        title: 'Invites',
        subtitle: 'Invite history for this Space — direct invites and shareable links.',
        create: 'New invite',
        search: 'Search by username or display name',
        sort: 'Sort',
        sortAria: 'Sort invite history',
        sortCreatedAt: 'Created',
        sortExpiresAt: 'Expiration',
        sortUseCount: 'Number of uses',
        sortAsc: 'Ascending',
        sortDesc: 'Descending',
        filtersAria: 'Filter by status',
        filtersAll: 'All statuses',
        filtersCount: '{{count}} statuses',
        filtersClear: 'Clear',
        filter: {
          pending: 'Pending',
          accepted: 'Accepted',
          declined: 'Declined',
          expired: 'Expired',
          revoked: 'Revoked',
          active: 'Active',
          exhausted: 'Used up',
        },
        kind: {
          direct: 'Direct invite',
          link: 'Invite link',
        },
        kindsAria: 'Show invite types',
        kindsAll: 'All types',
        kindsCount: '{{count}} types',
        kindsClear: 'Clear',
        hiddenLinks: "Invite links are hidden because they don't have {{statuses}} statuses.",
        hiddenDirects: "Direct invites are hidden because they don't have {{statuses}} statuses.",
        empty: 'No invites match these filters.',
        showing: 'Showing {{from}} to {{to}} of {{total}} results',
        pageFirst: 'First',
        pagePrev: 'Previous',
        pageNext: 'Next',
        pageLast: 'Last',
        pageOf: 'Page {{current}} of {{total}}',
        expand: 'Show who joined with this link',
        collapse: 'Hide who joined with this link',
        joinersTitle: 'Joined with this link',
        joinersEmpty: 'No one has joined with this link yet.',
        joinersSearch: 'Search joiners',
        joinersLoadError: "Couldn't load joiners.",
        by: 'by',
        expired: 'Expired',
        expiresIn: {
          d: 'Expires in {{count}}d',
          h: 'Expires in {{count}}h',
          m: 'Expires in {{count}}m',
        },
        usesUnlimited: '{{count}} used',
        usesCapped: '{{used}} / {{max}} used',
        temporaryBadge: 'Temporary · {{duration}}',
        channelsBadge: '{{count}} channel',
        channelsBadge_one: '{{count}} channel',
        channelsBadge_other: '{{count}} channels',
        channelsBadgeTooltip:
          'This invite grants access to the default channels, plus these additional channels:',
        unknownChannel: 'Unknown channel',
        state: {
          pending: 'Pending',
          accepted: 'Accepted',
          declined: 'Declined',
          active: 'Active',
          expired: 'Expired',
          revoked: 'Revoked',
          exhausted: 'Used up',
        },
        revoke: 'Revoke',
        revokeFailed: "Couldn't revoke that invite.",
        loadError: "Couldn't load invites.",
      },
      audit: {
        title: 'Audit log',
        subtitle: 'Moderation and configuration changes in this Space.',
        empty: 'No audit events yet.',
        loadError: "Couldn't load the audit log.",
        loadMore: 'Load more',
        by: 'by {{name}}',
        bySystem: 'by System',
        target: '· {{name}}',
        actions: {
          member_kick: 'Member kicked',
          member_ban: 'Member banned',
          member_unban: 'Member unbanned',
          member_roles_update: 'Member roles updated',
          role_create: 'Role created',
          role_update: 'Role updated',
          role_delete: 'Role deleted',
          channel_acl_update: 'Channel access updated',
          invite_create: 'Invite sent',
          invite_revoke: 'Invite revoked',
          invite_link_create: 'Invite link created',
          invite_link_revoke: 'Invite link revoked',
          temporary_access_expired: 'Temporary access expired',
          message_mod_delete: 'Message removed by moderator',
          space_profile_update: 'Space profile updated',
          space_theme_update: 'Space theme updated',
          announcement_post: 'Announcement posted',
          announcement_edit: 'Announcement edited',
          announcement_delete: 'Announcement deleted',
          activity_settings_update: 'Activity feed settings updated',
          space_create: 'Space created',
          space_settings_update: 'Space settings updated',
          channel_create: 'Channel created',
          channel_update: 'Channel updated',
          channel_move: 'Channel moved',
          channel_permissions_update: 'Channel permissions updated',
          category_create: 'Category created',
          category_update: 'Category updated',
          category_delete: 'Category deleted',
          category_permissions_update: 'Category permissions updated',
          channel_layout_update: 'Channels rearranged',
          role_reorder: 'Roles reordered',
          message_pin: 'Message pinned',
          message_unpin: 'Message unpinned',
          member_join: 'Member joined',
          member_leave: 'Member left',
          member_nickname_update: 'Member nickname changed',
          invite_expired: 'Invite expired',
          invite_link_expired: 'Invite link expired',
          member_note_create: 'Moderator note added',
          member_note_update: 'Moderator note edited',
          member_note_delete: 'Moderator note deleted',
        },
        unavailable: 'unavailable',
        fields: {
          name: 'Name',
          description: 'Description',
          logo: 'Logo',
          banner: 'Banner',
          encryption: 'Encryption',
          inheritAllowedRoleIds: 'Inherit roles',
          inheritCipherCheck: 'Inherit encryption',
          forceChildrenAcl: 'Force roles on children',
          forceChildrenCipher: 'Force encryption on children',
          position: 'Position',
          parentCategoryId: 'Parent category',
          nickname: 'Nickname',
          color: 'Color',
          visibility: 'Visibility',
          allowFreeMembers: 'Allow free members',
          cipherRequired: 'Cipher required',
        },
        via: {
          open: 'Joined directly',
          invite: 'Via invite',
          invite_link: 'Via invite link',
        },
        detail: {
          roles: 'Roles: {{roles}}',
          reason: 'Reason: {{reason}}',
          uncategorized: 'No category',
          reordered: 'Reordered',
          layout: '{{channels}} channels and {{categories}} categories moved',
          on: 'On',
          off: 'Off',
          themeCleared: 'Theme reset',
        },
      },
      danger: {
        title: 'Danger zone',
        body: 'Deleting a Space permanently removes channels, messages, roles, and membership. This cannot be undone.',
        deleteCta: 'Delete Space',
      },
      deleteConfirm: {
        title: 'Delete this Space?',
        description:
          'This permanently deletes {{name}} and all of its content. Type DELETE to confirm.',
        typeLabel: 'Type DELETE to confirm',
        confirm: 'Delete Space',
      },
      deleteSuccess: '{{name}} was deleted.',
      deleteError: "Couldn't delete this Space. Please try again.",
      roles: {
        title: 'Roles',
        subtitle: 'Create roles and control what members can do in this Space.',
        loadError: "Couldn't load roles.",
        notFound: 'That role was not found.',
        create: 'Create role',
        creating: 'Creating\u2026',
        createSuccess: 'Role created.',
        createError: "Couldn't create the role.",
        newRoleName: 'New Role',
        delete: 'Delete Role',
        deleteSystemTooltip:
          "System roles can't be deleted, but you can still customize this role.",
        deleteSuccess: 'Role deleted.',
        deleteError: "Couldn't delete the role.",
        deleteInUseError: 'Remove all members from this system role before deleting it.',
        deleteConfirmTitle: 'Delete this role?',
        deleteConfirmBody: 'Members will lose "{{name}}" and its permissions.',
        systemBadge: 'System',
        adminName: 'Admin',
        memberName: 'Everyone',
        emptyEditor: 'Select a role to edit, or create a new one.',
        selectRole: 'Select a role',
        backToList: 'All roles',
        save: 'Save changes',
        saving: 'Saving\u2026',
        saveError: "Couldn't save role changes.",
        preview: 'Preview Space as this role',
        previewActive: 'Previewing this Space as the selected role.',
        exitPreview: 'Exit preview',
        tabs: {
          settings: 'Settings',
          permissions: 'Permissions',
          members: 'Manage Members',
        },
        settings: {
          name: 'Role name',
          color: 'Role color',
          description: 'Role description',
          descriptionPlaceholder: 'What is this role for?',
          descriptionHint: 'Shown when someone hovers this role in chat or the member list.',
          displaySeparately: 'Display role members separately',
          displaySeparatelyHint: 'Show members with this role in their own group in the member list.',
          mentionable: 'Allow anyone to mention this role',
          mentionableHint: 'Members without special permissions can @mention this role.',
          everyoneAlwaysDefault:
            'Everyone is always assigned to every member and is the permanent default join role.',
          clearColor: 'Remove color',
          colorNone: 'None',
          saveColor: 'Save color',
          previousColor: 'Prev',
          resizeDescription: 'Resize description',
        },
        members: {
          addLabel: 'Add member',
          addPlaceholder: 'Select a member\u2026',
          addSearchPlaceholder: 'Search members\u2026',
          addNoResults: 'No members found.',
          add: 'Add',
          remove: 'Remove from role',
          empty: 'No members have this role yet.',
          membersUpdateError: "Couldn't update member roles.",
          lastAdminError: 'Cannot remove the last Admin.',
          rolesLabel: 'Roles',
          defaultRoleHint:
            'Every member keeps the default role, so it cannot be removed from individuals here.',
          everyoneHint:
            'All members of this Space inherit Everyone permissions. These are the permissions every member gets by default.',
        },
        inviteRoles: 'Invite Roles',
        inviteRolesHint: 'These roles are auto-generated by direct invites.',
        reorderError: "Couldn't save role order.",
      },
    },
    permissions: {
      toggle: {
        no: 'No',
        yes: 'Yes',
        manage: 'Manage',
      },
      categories: {
        general: 'General',
        home: 'Home',
        members: 'Members',
        text: 'Text',
        voice: 'Audio/Video',
        events: 'Events',
        empty: 'Coming soon',
        count: '{{count}} permissions',
        comingSoon: 'Permissions for this category will be added later.',
      },
      viewChannels: {
        title: 'View Channels',
        description: 'Allows members to view channels in this Space.',
      },
      manageChannels: {
        title: 'Manage Channels',
        description: 'Allows members to create and manage channels in this Space.',
      },
      manageRoles: {
        title: 'Manage Roles',
        description: 'Allows members to create and edit roles below their own.',
      },
      viewAuditLog: {
        title: 'View Audit Log',
        description: 'Allows members to view the Space audit log.',
      },
      manageWebhooks: {
        title: 'Manage Webhooks',
        description: 'Allows members to create, edit, and delete webhooks.',
      },
      manageEncryption: {
        title: 'Manage Encryption',
        description: 'Allows members to manage Space encryption and Cipher settings.',
      },
      manageMetadata: {
        title: 'Manage Metadata',
        description: 'Allows members to change Space visibility, free-member access, and the Cipher join gate.',
      },
      editProfile: {
        title: 'Edit Space Profile',
        description:
          'Allows members to change the Space name, description, logo, and banner. Includes View Avatar, View Bio, and View Banner.',
      },
      viewSpaceAvatar: {
        title: 'View Avatar',
        description:
          "Allows seeing the Space logo. Set on Everyone to decide what signed-out visitors see. A logo someone already loaded can stay reachable by its link until it's replaced.",
      },
      viewSpaceBio: {
        title: 'View Bio',
        description: 'Allows seeing the Space description. Set on Everyone to decide what signed-out visitors see.',
      },
      viewSpaceBanner: {
        title: 'View Banner',
        description:
          "Allows seeing the Space banner. Set on Everyone to decide what signed-out visitors see. A banner someone already loaded can stay reachable by its link until it's replaced.",
      },
      includedWith: 'Included with {{permission}}',
      editTheme: {
        title: 'Edit Space Theme',
        description: 'Allows members to change the colors used across the Space.',
      },
      deleteSpace: {
        title: 'Delete Space',
        description: 'Allows members to permanently delete the Space and all of its content.',
      },
      manageAnnouncements: {
        title: 'Manage Announcements',
        description: 'Allows members to post, edit, and delete any announcement on the Space Home.',
      },
      manageActivity: {
        title: 'Manage Activity',
        description:
          'Allows members to change how the Space Home activity feed is ranked and its default sort.',
      },
      directInviteFriends: {
        title: 'Invite Friends',
        description: 'Allows members to invite Aliases they are already friends with.',
      },
      directInviteAnyone: {
        title: 'Invite Anyone',
        description: 'Allows members to invite any Alias, whether or not they are friends.',
      },
      createInviteLink: {
        title: 'Create Invite Link',
        description: 'Allows members to create a shareable link that anyone holding it can use to join.',
      },
      manageInvites: {
        title: 'Manage Invites',
        description: 'Allows members to view and revoke invites and invite links created by others.',
      },
      manageMembers: {
        title: 'Manage Members',
        description:
          'Opens the Members page: browse, search, and filter current, banned, and former members, and read and write moderator notes. Kicking, banning, and role changes still need their own permissions.',
      },
      changeNickname: {
        title: 'Change Nickname',
        description: 'Yes: change own nickname. Manage: change other members\u2019 nicknames.',
      },
      kickMembers: {
        title: 'Kick Members',
        description: 'Allows members to remove other members from the Space.',
      },
      banMembers: {
        title: 'Ban Members',
        description: 'Allows members to ban other members from the Space.',
      },
      manageMemberRoles: {
        title: 'Manage Member Roles',
        description: 'Allows members to assign and remove roles on other members (only roles with permissions they hold).',
      },
      manageApplications: {
        title: 'Approve Applications',
        description: 'Allows members to approve or reject join applications.',
      },
      sendMessages: {
        title: 'Send Messages',
        description: 'Yes: send messages. Manage: edit or delete messages sent by others.',
      },
      embedLinks: {
        title: 'Embed Links',
        description: 'Allows links in messages to show embedded content previews.',
      },
      attachFiles: {
        title: 'Attach Files',
        description: 'Allows members to upload files in messages.',
      },
      addReactions: {
        title: 'Add Reactions',
        description: 'Allows members to add reactions to messages.',
      },
      useCustomEmoji: {
        title: 'Use Custom Emoji',
        description: 'Allows members to use custom emoji in messages and reactions.',
      },
      useGifsStickers: {
        title: 'Use GIFs / Stickers',
        description: 'Allows members to send GIFs and stickers.',
      },
      mentionHere: {
        title: 'Mention @here',
        description: 'Allows members to use @here to notify everyone who can view the channel.',
      },
      mentionRoles: {
        title: 'Mention Roles',
        description: 'Allows members to mention roles that are not marked as mentionable by everyone.',
      },
      pinMessages: {
        title: 'Pin Messages',
        description: 'Allows members to pin and unpin messages in channels.',
      },
      bypassSlowmode: {
        title: 'Bypass Slowmode',
        description: 'Allows members to send messages without waiting for the slowmode cooldown.',
      },
      connect: {
        title: 'Connect',
        description: 'Allows members to join Audio/Video channels.',
      },
      speak: {
        title: 'Speak',
        description: 'Allows members to speak in Audio/Video channels.',
      },
      video: {
        title: 'Video',
        description: 'Allows members to share their camera in Audio/Video channels.',
      },
      stream: {
        title: 'Screen Share',
        description: 'Allows members to share their screen in Audio/Video channels.',
      },
      muteMembers: {
        title: 'Mute Members',
        description: 'Allows members to mute other members in Audio/Video channels.',
      },
    },
    members: {
      kick: 'Kick',
      ban: 'Ban',
      updateRoles: 'Update roles',
      everyone: 'Everyone',
      admin: 'Admin',
      roleCount: '{{count}} roles',
      roleCount_one: '1 role',
      roleCount_other: '{{count}} roles',
      removedSuffix: '[Removed]',
      noLongerInSpace: 'User is no longer part of the Space',
      deletedUser: 'Deleted user',
      temporaryLabel: 'Temporary Space member',
      temporaryTooltip: 'Temporary membership ends {{when}}',
      temporaryExpired: 'Expired',
      nicknameAndColor: 'Nickname & Color',
      mention: 'Mention',
      mentionDisabled: 'You cannot mention here.',
      copyUserId: 'Copy User ID',
      copyUserIdSuccess: 'User ID copied.',
      copyUserIdFailed: 'Could not copy User ID.',
      kickDisabledSelf: 'You cannot kick yourself.',
      kickDisabledOwner: 'The Space owner cannot be kicked.',
      kickDisabledRank: 'You cannot kick a member with an equal or higher role.',
      kickDisabledLastAdmin: 'You cannot kick the only admin.',
      banDisabledSelf: 'You cannot ban yourself.',
      banDisabledOwner: 'The Space owner cannot be banned.',
      banDisabledRank: 'You cannot ban a member with an equal or higher role.',
      banDisabledLastAdmin: 'You cannot ban the only admin.',
      lastAdminRoleLocked: 'You cannot remove the Admin role while you are the only admin.',
      roleEscalationLocked: 'You cannot assign a role with permissions you do not hold.',
      adminRoleAssignLocked: 'Only system admins can assign the Admin role.',
      kickTitle: 'Kick member?',
      kickConfirm: 'Kick',
      banTitle: 'Ban member?',
      banConfirm: 'Ban',
      reason: 'Reason',
      banDuration: 'Duration',
      banDurations: {
        '1h': '1 hour',
        '1d': '1 day',
        '7d': '7 days',
        '30d': '30 days',
        permanent: 'Permanent',
      },
      kickFailed: 'Could not kick member.',
      banFailed: 'Could not ban member.',
      rolesUpdateFailed: 'Could not update roles.',
      profileSaveFailed: 'Could not save member settings.',
      needsManageMemberRoles: 'You need the Manage Member Roles permission to assign roles.',
    },
    channel: {
      noMessages: 'No messages yet. Start the conversation!',
      loadOlder: 'Load older messages',
      e2eeComingSoon: 'Encrypted messaging for Spaces is coming soon.',
      noCipher: 'You need the matching Community Cipher to read and send messages in this channel.',
      checkCiphers: 'Check against my current Ciphers',
      addCipher: 'Add a Cipher',
      matchingCiphers: 'Checking this channel against your Ciphers\u2026',
      checkingCiphers: 'Checking\u2026',
      cipherFound: 'Matching Cipher found. You can read and send messages now.',
      cipherStillMissing: 'No matching Cipher found among your current Ciphers.',
      joinOrLurk: 'Join this Space to post, or Lurk to stay read only.',
      lurkingSince: "You've been a lurker since {{date}}. Want to get more involved?",
      lurkingInvolved: "You're lurking this Space. Want to get more involved?",
      lurk: 'Lurk',
      lurkTooltip:
        "Lurking saves this Space to your sidebar but doesn't join the Space. Lurkers are invisible to the Space and are read-only.",
      joinCta: 'Join Space',
    },
    voice: {
      barLabel: 'Audio/Video channel controls',
      statusLive: 'Connected',
      statusConnecting: 'Connecting\u2026',
      statusWaiting: 'Waiting for others',
      mute: 'Mute',
      unmute: 'Unmute',
      cameraOn: 'Camera',
      cameraOff: 'Camera off',
      disconnect: 'Disconnect',
      join: 'Join Call',
      leave: 'Leave Call',
      channelFallback: 'Audio/Video channel',
      joinFailed: 'Could not join the Audio/Video channel.',
      cipherRequired: 'You need the matching Community Cipher to join this encrypted Audio/Video channel.',
    },
  },

  threads: {
    replyInThread: 'Reply in thread',
    repliedInThread: 'Replied in a thread',
    replyCount_one: '{{count}} reply',
    replyCount_other: '{{count}} replies',
    participantCount_one: '{{count}} participant',
    participantCount_other: '{{count}} participants',
    ephemeralCount_one: '{{count}} disappearing',
    ephemeralCount_other: '{{count}} disappearing',
    lastReply: 'Last reply {{time}}',
    panelTitle: 'Thread',
    channelTarget: '#{{channel}}',
    close: 'Close thread',
    popout: 'Open thread in a new window',
    dock: 'Return thread to the main window',
    popoutBlocked: 'Could not open a new window for this thread.',
    resize: 'Resize thread panel',
    loadOlder: 'Load older replies',
    loading: 'Loading thread…',
    loadFailed: 'Could not load this thread.',
    notFound: 'This thread is no longer available.',
    retry: 'Retry',
    noReplies: 'No replies yet. Start the conversation.',
    composerPlaceholder: 'Reply…',
    alsoSendToChannel: 'Also send to #{{channel}}',
    alsoSendToConversation: 'Also send to the conversation',
    sendFailed: 'Could not send your reply.',
  },

  siteAnnouncement: {
    dismiss: 'Dismiss',
    noticeButton: 'Notice',
  },
} as const;
